import { IMetaData } from "../components/controls/frontend-productlist/types";

type DocumentInfoSetterUpdateMethod = (
    document: Document,
    value: string | null | undefined
) => void;
type DocumentInfoSetterGetMethod = (
    document: Document
) => string | null | undefined;

class DocumentInfoSetter {
    name: string;
    update: DocumentInfoSetterUpdateMethod;
    get: DocumentInfoSetterGetMethod;
    originalValue: undefined | null | string;
    originalValueSet: boolean;
    constructor(
        name: string,
        update: DocumentInfoSetterUpdateMethod,
        get: DocumentInfoSetterGetMethod
    ) {
        this.name = name;
        this.update = update;
        this.get = get;
        this.originalValue = null;
        this.originalValueSet = false;
    }

    set(value: string) {
        console.debug("Setting " + this.name + " to " + JSON.stringify(value));
        if (!this.originalValueSet) {
            this.originalValueSet = true;
            this.originalValue = this.get(document);
            console.debug(
                "Stored original " +
                    this.name +
                    " (" +
                    JSON.stringify(this.originalValue) +
                    ")"
            );
        }
        this.update(document, value);
    }

    reset() {
        if (this.originalValueSet) {
            console.debug(
                "Resetting " +
                    this.name +
                    " back to " +
                    JSON.stringify(this.originalValue)
            );
            this.update(document, this.originalValue);
        } else {
            console.debug(
                "Would've reset " + this.name + " but no original value is set"
            );
        }
    }
}

function arr<T extends Node>(list: T[] | NodeListOf<T>) {
    const result = [];
    for (let i = 0; i < list.length; i++) {
        result.push(list[i]);
    }
    return result;
}

const titleSetter = new DocumentInfoSetter(
    "title",
    function (doc, title) {
        document.title = title ?? "";
        arr(doc.querySelectorAll("head>title")).forEach(
            (el) => (el.textContent = title ?? null)
        );
        arr(
            doc.querySelectorAll<HTMLMetaElement>("meta[property='og:title']")
        ).forEach((el) => (el.content = title ?? ""));
    },
    function (doc) {
        return doc.title;
    }
);

export function getTitle() {
    return titleSetter.get(document);
}

export function resetTitle() {
    titleSetter.reset();
}

export function setTitle(title: string) {
    titleSetter.set(title);
}

const descriptionSetter = new DocumentInfoSetter(
    "description",
    function (doc, description) {
        arr(
            doc.querySelectorAll<HTMLMetaElement>("meta[name='description']")
        ).forEach((el) => (el.content = description ?? ""));
        arr(
            doc.querySelectorAll<HTMLMetaElement>(
                "meta[property='og:description']"
            )
        ).forEach((el) => (el.content = description ?? ""));
    },
    function (doc) {
        const el = doc.querySelector<HTMLMetaElement>(
            "meta[name='description']"
        );
        if (el == null) return undefined;
        return el.content;
    }
);

export function handleMetaResult(
    result: Partial<IMetaData> | null | undefined
) {
    if (result != null && typeof result.titleTag === "string") {
        setTitle(result.titleTag);
    } else {
        resetTitle();
    }
    if (result != null && typeof result.metaDesc === "string") {
        setDescription(result.metaDesc);
    } else {
        resetDescription();
    }
}

export function getDescription() {
    descriptionSetter.get(document);
}

export function setDescription(description: string) {
    descriptionSetter.set(description);
}

export function resetDescription() {
    descriptionSetter.reset();
}

const banishedQueryParamKeys = ["sort"];

const canonicalLinkSetter = new DocumentInfoSetter(
    "canonical url",
    function (doc, url) {
        if (url != null) {
            const urlObj = new URL(url);
            for (const key of banishedQueryParamKeys) {
                urlObj.searchParams.delete(key);
            }
            url = urlObj.toString();
        }
        arr(
            doc.querySelectorAll<HTMLLinkElement>("link[rel='canonical']")
        ).forEach((el) => (el.href = url ?? ""));
        arr(
            doc.querySelectorAll<HTMLMetaElement>("meta[property='og:url']")
        ).forEach((el) => (el.content = url ?? ""));
    },
    function (doc) {
        return (
            doc.querySelector<HTMLLinkElement>("link[rel='canonical']")?.href ??
            doc.querySelector<HTMLMetaElement>("meta[property='og:url']")
                ?.content ??
            doc.location.href
        );
    }
);

export function setCanonicalLink(url: string) {
    canonicalLinkSetter.set(url);
}

export function resetCanonicalLink() {
    canonicalLinkSetter.reset();
}
